import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import type {OptionalKeysOf} from 'type-fest'

import clearQuotes from '@/utils/clearQuotes'
import parseNumber from '@/utils/numbers/parseNumber'

// Follow: https://source.cloud.google.com/solid-justice-415604/innosearch-scrapers/+/master:scrapers/sites.py
export enum Retailer {
  Walmart = 'walmart',
  Amazon = 'amazon',
  GoogleShopping = 'google_shopping',
  BestBuy = 'bestbuy',
  Wayfair = 'wayfair',
  IKEA = 'ikea',
  Costco = 'costco',
  HomeDepot = 'homedepot',
  eBay = 'ebay',
  Etsy = 'etsy',
  TJMaxx = 'tjmaxx',
  Walgreens = 'walgreens',
  Nike = 'nike',
  Wish = 'wish',
  Macys = 'macys',
  Target = 'target',
}

export const retailers = Object.values(Retailer)

export enum Program {
  AmazonPrime = 'prime',
  WalmartPlus = 'w+',
}

export const programs = Object.values(Program)

// Can contains `"` and much more
export const rawProductSchema = Type.Object({
  retailer: Type.String(),
  seller: Type.Optional(Type.String()),
  url: Type.Optional(Type.String()),
  retailer_url: Type.Optional(Type.String()),
  image: Type.String(),
  title: Type.String(),
  price: Type.Union([Type.String(), Type.Number()]),
  rating: Type.Optional(Type.Union([Type.String(), Type.Number()])),
  rating_count: Type.Optional(Type.Union([Type.String(), Type.Number()])),
  org_price: Type.Optional(Type.Union([Type.String(), Type.Number()])),
  program: Type.Optional(Type.String()),
  id: Type.String(),
  manufacturer: Type.Optional(Type.Union([Type.String(), Type.Number()])),
  shipping: Type.Optional(Type.String()),
  return: Type.Optional(Type.String()),
  shipping_information: Type.Optional(Type.Union([Type.String(), Type.Array(Type.String())])),
})

export type RawProduct = Static<typeof rawProductSchema>
const rawProductTypeCheck = TypeCompiler.Compile(rawProductSchema)

export type RawMinimalProduct = Pick<RawProduct, 'retailer' | 'id'>

const rawProductReview = Type.Object({
  author: Type.Union([Type.String(), Type.Null()]),
  title: Type.Optional(Type.String()),
  content: Type.String(),
  id: Type.Optional(Type.String()),
  rating: Type.Optional(Type.Union([Type.String(), Type.Number()])),
  timestamp: Type.String(),
})

const rawProductVariationSchema = Type.Composite([
  Type.Object({
    id: Type.String(),
    out_of_stock: Type.Optional(
      Type.Boolean({
        default: false,
      }),
    ),
  }),
  Type.Mapped(Type.String(), () => Type.String()),
])

type RawProductVariation = Static<typeof rawProductVariationSchema>

const rawProductDetailsSchema = Type.Object({
  currency: Type.String(),
  description: Type.Optional(
    Type.Union([
      Type.Null(),
      Type.String({
        default: '',
      }),
    ]),
  ),
  images: Type.Optional(Type.Union([Type.Null(), Type.Array(Type.String(), {default: []})])),
  out_of_stock: Type.Optional(
    Type.Union([
      Type.Null(),
      Type.Boolean({
        default: false,
      }),
    ]),
  ),
  reviews: Type.Optional(Type.Union([Type.Array(rawProductReview), Type.Null()])),
  variations: Type.Union([Type.Array(rawProductVariationSchema), Type.Object({})]),
  return: Type.Optional(Type.String()),
  shipping: Type.Optional(Type.String()),
  available_quantity: Type.Optional(Type.Number()),
})

export const rawFullProductSchema = Type.Composite([rawProductSchema, rawProductDetailsSchema])
export type RawFullProduct = Static<typeof rawFullProductSchema>
const rawFullProductTypeCheck = TypeCompiler.Compile(rawFullProductSchema)

const rawMultiFullProductSchema = Type.Object({
  metadata: Type.Object({
    thread_id: Type.Optional(Type.String()),
  }),
  products: Type.Array(Type.Unknown(), {
    minItems: 1,
  }),
  suggested_prompts: Type.Optional(Type.Array(Type.String())),
})

export type RawMultiFullProduct = Static<typeof rawMultiFullProductSchema>
const rawMultiFullProductTypeCheck = TypeCompiler.Compile(rawMultiFullProductSchema)

// Remember to update `productsToCsv` serialization to include more product information when copy
export interface Product
  extends Omit<
    RawProduct,
    'rating' | 'rating_count' | 'org_price' | 'program' | 'retailer' | 'shipping_information'
  > {
  rating: number
  ratingCount: number
  orgPrice?: number | string
  program?: Program
  retailer: Retailer | string
  return?: string
  shipping?: string
  shippingInformation?: string[]
}

export interface FullProduct extends Product {
  currency: string
  description: string
  images: string[]
  outOfStock: boolean
  reviews: {
    author: string
    title: string
    content: string
    id: string
    rating: number
    timestamp: Date
  }[]
  variations: {
    id: string
    outOfStock: boolean
    values: Map<string, string>
  }[]
  return?: string
  shipping?: string
  availableQuantity?: number
}

export interface MultiFullProduct {
  threadId?: string
  products: FullProduct[]
  suggestedPrompts: string[]
}

const PRODUCT_OPTIONAL_PROPS_MAP: Record<OptionalKeysOf<Product>, true> = {
  manufacturer: true,
  orgPrice: true,
  program: true,
  return: true,
  shipping: true,
  shippingInformation: true,
  seller: true,
  url: true,
  retailer_url: true,
}
export const PRODUCT_OPTIONAL_PROPS = Object.keys(PRODUCT_OPTIONAL_PROPS_MAP)

const rawBookmarkedProductSchema = Type.Composite([
  rawProductSchema,
  Type.Object({
    creation_time: Type.String(),
  }),
])

const rawBookmarkedProductTypeCheck = TypeCompiler.Compile(rawBookmarkedProductSchema)

export type RawBookmarkedProduct = Static<typeof rawBookmarkedProductSchema>

export interface BookmarkedProduct extends Product {
  creationTime: Date
}

export function nomalizeProductId(id: RawProduct['id']) {
  return clearQuotes(id)
}

export function isSupportedRetailer(retailer: RawProduct['retailer']): retailer is Retailer {
  const cleanedRetailer = clearQuotes(retailer)
  return retailers.includes(cleanedRetailer)
}

export function parseRetailer(retailer: RawProduct['retailer']): Retailer | string {
  return clearQuotes(retailer).toLowerCase()

  // if (isSupportedRetailer(cleanedRetailer)) {
  //   return cleanedRetailer
  // } else {
  //   Throw error because retailer must be precisely defined
  //   throw new Error('Retailer: ' + retailer + ' is not support')
  // }
}

export function parseProgram(program: RawProduct['program'], warn?: boolean) {
  let cleanedProgram = program ? (clearQuotes(program) as Program) : undefined

  if (cleanedProgram) {
    if (programs.includes(cleanedProgram)) {
      return cleanedProgram
    }
    // Don't throw error because program is not mandatory
    if (warn) console.warn(`Program: ${program} is not support`)
    // console.error('Program: ' + program + ' is not support')
    // throw new Error('Program: ' + program + ' is not support')
    cleanedProgram = undefined
  }

  return cleanedProgram
}

//----------------------------------------------------------------------------------------------------------------------

export function $transformProduct<T extends RawProduct = RawProduct | RawBookmarkedProduct>(
  rawProduct: T,
  warn?: boolean,
): T extends RawBookmarkedProduct ? BookmarkedProduct | false : Product | false {
  let parsedProductRating = Number.NaN
  let parsedProductRatingCount = Number.NaN
  let parsedProductPrice: number | string
  let parsedProductOrgPrice: number | string
  let parsedRetailer: Product['retailer']
  let parsedSeller: Product['seller']
  let parsedProgram: Product['program']
  let parsedReturn = ''
  let parsedShipping = ''
  let parsedShippingInformation: string[] = []

  // Product is considered invalid if the following information is not valid
  try {
    parsedProductPrice = parseNumber(clearQuotes(String(rawProduct.price))) || Number.NaN
  } catch {
    parsedProductPrice = Number.NaN
  }

  if (Number.isNaN(parsedProductPrice)) {
    parsedProductPrice = rawProduct.price
  }

  try {
    parsedRetailer = parseRetailer(rawProduct.retailer)
  } catch (e) {
    console.error(e)
    return false
  }

  // Product is still fine without these information
  if (rawProduct.seller) {
    try {
      parsedSeller = parseRetailer(rawProduct.seller)
    } catch (e) {
      if (warn) console.warn(e)
    }
  }

  try {
    parsedProductRating = parseNumber(clearQuotes(String(rawProduct.rating))) || Number.NaN
    parsedProductRatingCount =
      parseNumber(clearQuotes(String(rawProduct.rating_count))) || Number.NaN
  } catch (e) {
    if (warn) console.warn(e)
  }

  try {
    parsedProductOrgPrice = rawProduct.org_price
      ? parseNumber(clearQuotes(String(rawProduct.org_price)))
      : Number.NaN
  } catch {
    parsedProductOrgPrice = Number.NaN
  }

  if (Number.isNaN(parsedProductOrgPrice)) {
    parsedProductOrgPrice = rawProduct.org_price ?? Number.NaN
  }

  try {
    parsedProgram = parseProgram(rawProduct.program)
  } catch (e) {
    if (warn) console.warn(e)
  }

  try {
    if (rawProduct.return) parsedReturn = clearQuotes(String(rawProduct.return))
  } catch (e) {
    if (warn) console.warn(e)
  }

  try {
    if (rawProduct.shipping) parsedShipping = clearQuotes(String(rawProduct.shipping))
  } catch (e) {
    if (warn) console.warn(e)
  }

  try {
    if (rawProduct.shipping_information)
      parsedShippingInformation = Array.isArray(rawProduct.shipping_information)
        ? rawProduct.shipping_information.map(item => clearQuotes(String(item)))
        : [clearQuotes(String(rawProduct.shipping_information))]
  } catch (e) {
    if (warn) console.warn(e)
  }
  // END

  const rawUrl = rawProduct.url ?? rawProduct.retailer_url

  const product: Product = {
    title: clearQuotes(rawProduct.title),
    image: clearQuotes(rawProduct.image),
    id: nomalizeProductId(rawProduct.id),
    retailer: parsedRetailer,
    seller: parsedSeller,
    program: parsedProgram,
    rating: parsedProductRating,
    ratingCount: parsedProductRatingCount,
    price: parsedProductPrice,
    orgPrice: parsedProductOrgPrice,
    return: parsedReturn,
    shipping: parsedShipping,
    shippingInformation: parsedShippingInformation,
  }

  if (rawUrl) {
    product.url = clearQuotes(rawUrl)
  }

  if ('creation_time' in rawProduct) {
    ;(product as BookmarkedProduct).creationTime = new Date(
      (rawProduct as unknown as RawBookmarkedProduct).creation_time,
    )
  }

  if (isProductBroken(product)) {
    return false
  }

  return product as T extends RawBookmarkedProduct ? BookmarkedProduct : Product
}

export function parseVariations(rawVariations: RawFullProduct['variations']) {
  return Array.isArray(rawVariations)
    ? rawVariations
        .map(variation => {
          // TODO: investigate why typeguard Array.isArray(rawVariations) dont work
          const newVariation = variation as RawProductVariation

          const values = new Map(Object.entries(newVariation)) as Map<string, string>

          values.delete('id')
          values.delete('out_of_stock')

          return {
            values,
            id: clearQuotes(newVariation.id),
            outOfStock: newVariation.out_of_stock ?? false,
          }
        })
        .toSorted(
          (a, b) =>
            Number(
              a.id
                .split('')
                .map(str => str.charCodeAt(0))
                .join(''),
            ) -
            Number(
              b.id
                .split('')
                .map(str => str.charCodeAt(0))
                .join(''),
            ),
        )
    : []
}

export function __transformFullProduct(
  rawFullProduct: RawFullProduct,
  warn?: boolean,
): FullProduct | false {
  const product = $transformProduct(rawFullProduct, warn)
  if (!product) return false

  const description = clearQuotes(rawFullProduct.description ?? '')
  const currency = rawFullProduct.currency
  const images =
    rawFullProduct.images && rawFullProduct.images.length > 0
      ? Array.from(new Set(rawFullProduct.images)) // Remove duplicated image
      : [product.image]
  const outOfStock = rawFullProduct.out_of_stock ?? false
  const reviews =
    rawFullProduct.reviews?.map(review => ({
      author: review.author ? clearQuotes(review.author) : '',
      title: clearQuotes(review.title ?? ''),
      content: clearQuotes(review.content),
      id: review.id
        ? clearQuotes(review.id)
        : review.author +
          review.timestamp +
          review.rating +
          review.content.slice(0, 5) +
          review.content.slice(20, 25), // Try to create a unique id based on the available information
      rating: parseNumber(clearQuotes(String(review.rating))) || Number.NaN,
      timestamp: new Date(review.timestamp),
    })) ?? []
  const variations = parseVariations(rawFullProduct.variations)
  const returnPolicy = clearQuotes(rawFullProduct.return ?? '')
  const shipping = clearQuotes(rawFullProduct.shipping ?? '')
  const availableQuantity = rawFullProduct.available_quantity

  return {
    ...product,
    description,
    currency,
    images,
    outOfStock,
    reviews,
    variations,
    shipping,
    return: returnPolicy,
    availableQuantity,
  }
}

export function isMinimalProductBroken(rawMinimalProduct: Partial<RawMinimalProduct>) {
  return Object.entries(rawMinimalProduct).some(([key, value]: [string, string | undefined]) => {
    if (PRODUCT_OPTIONAL_PROPS.includes(key)) return false
    return value === undefined || value === ''
  })
}

function isProductBroken(product: Product | BookmarkedProduct) {
  if (
    Number.isNaN(product.price) ||
    product.title.length === 0 ||
    ((!product.url || product.url.length === 0) &&
      (!product.retailer_url || product.retailer_url.length === 0))
  ) {
    console.error('Product data broken', product)
    return true
  }

  if ('creationTime' in product && Number.isNaN(product.creationTime.valueOf())) {
    return false
  }

  return false
}

//----------------------------------------------------------------------------------------------------------------------

export function transformProduct(rawProduct: unknown): Product | false {
  if (!rawProductTypeCheck.Check(rawProduct)) {
    console.error('Invalid product', rawProduct, ...rawProductTypeCheck.Errors(rawProduct))
    return false
  }

  return $transformProduct(rawProduct, true)
}

export function transformBookmarkedProduct(rawProduct: unknown): BookmarkedProduct | false {
  if (!rawBookmarkedProductTypeCheck.Check(rawProduct)) {
    console.error(
      'Invalid bookmarked product',
      rawProduct,
      ...rawBookmarkedProductTypeCheck.Errors(rawProduct),
    )
    return false
  }

  return $transformProduct(rawProduct, true)
}

export function transformProducts(rawProducts: unknown[]): Product[] {
  const seen = new Map<string, true>()
  return rawProducts
    .map(product => transformProduct(product))
    .filter(Boolean)
    .filter(product => {
      const key = `${product.retailer}-${product.id}`
      if (seen.has(key)) return false
      seen.set(key, true)
      return true
    })
}

export function transformBookmarkedProducts(rawProducts: unknown[]): BookmarkedProduct[] {
  const seen = new Map<string, true>()
  return rawProducts
    .map(product => transformBookmarkedProduct(product))
    .filter(Boolean)
    .filter(product => {
      const key = `${product.retailer}-${product.id}`
      if (seen.has(key)) return false
      seen.set(key, true)
      return true
    })
}

export function transformFullProduct(rawFullProduct: unknown): FullProduct | false {
  if (!rawFullProductTypeCheck.Check(rawFullProduct)) {
    console.error(
      'Invalid full product',
      rawFullProduct,
      ...rawFullProductTypeCheck.Errors(rawFullProduct),
    )
    return false
  }

  return __transformFullProduct(rawFullProduct, true)
}

export function transformFullProducts(rawFullProducts: unknown[]): FullProduct[] {
  const seen = new Map<string, true>()
  return rawFullProducts
    .map(product => transformFullProduct(product))
    .filter(Boolean)
    .filter(product => {
      const key = `${product.retailer}-${product.id}`
      if (seen.has(key)) return false
      seen.set(key, true)
      return true
    })
}

export function transformMultiFullProduct(
  rawMultiFullProduct: unknown,
  minimal = false,
): false | MultiFullProduct {
  if (!minimal) {
    if (!rawMultiFullProductTypeCheck.Check(rawMultiFullProduct)) {
      console.error(
        'Invalid multi full product',
        rawMultiFullProduct,
        ...rawMultiFullProductTypeCheck.Errors(rawMultiFullProduct),
      )
      return false
    }

    const products = transformFullProducts(rawMultiFullProduct.products)

    if (products.length === 0) return false

    return {
      threadId: rawMultiFullProduct.metadata.thread_id,
      products,
      suggestedPrompts: rawMultiFullProduct.suggested_prompts ?? [],
    }
  }

  if (rawMultiFullProduct === null) return false

  if (typeof rawMultiFullProduct !== 'object' || !('products' in rawMultiFullProduct)) return false

  if (!Array.isArray(rawMultiFullProduct.products)) return false

  const products = transformFullProducts([...rawMultiFullProduct.products])

  if (products.length === 0) return false

  return {
    threadId: '',
    products,
    suggestedPrompts: [],
  }
}
export function hashProduct(product: Pick<Product, 'id' | 'retailer'>) {
  return `${product.retailer}-${product.id}`
}
